import React, { useEffect, useRef, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { Translate, CaretDown, Check } from '@phosphor-icons/react';
import { useTranslation } from '../../utils/i18n';
import { useLanguage } from '../../contexts/LanguageContext';

const LANGUAGES = [
  { code: 'fr', countryCode: 'FR' },
  { code: 'en', countryCode: 'US' },
] as const;

const AuthLanguageSwitcher: React.FC = () => {
  const { t } = useTranslation('auth');
  const { currentLanguage, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const active =
    LANGUAGES.find((language) => language.code === currentLanguage) ?? LANGUAGES[0];

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const selectLanguage = (code: string) => {
    setLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="auth-lang-switcher" ref={containerRef}>
      <button
        type="button"
        className="auth-lang-trigger"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('switcher.label')}
      >
        <Translate size={18} weight="bold" aria-hidden />
        <ReactCountryFlag
          countryCode={active.countryCode}
          svg
          aria-hidden
          style={{ width: '1.15em', height: '1.15em', borderRadius: '2px' }}
        />
        <span>{t(`switcher.${active.code}`)}</span>
        <CaretDown
          size={14}
          weight="bold"
          aria-hidden
          className={`auth-lang-caret ${isOpen ? 'auth-lang-caret--open' : ''}`}
        />
      </button>

      {isOpen && (
        <ul className="auth-lang-menu" role="listbox" aria-label={t('switcher.label')}>
          {LANGUAGES.map((language) => {
            const selected = language.code === active.code;
            return (
              <li key={language.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className="auth-lang-option"
                  onClick={() => selectLanguage(language.code)}
                >
                  <ReactCountryFlag
                    countryCode={language.countryCode}
                    svg
                    aria-hidden
                    style={{ width: '1.3em', height: '1.3em', borderRadius: '2px' }}
                  />
                  <span>{t(`switcher.${language.code}`)}</span>
                  {selected && (
                    <Check size={16} weight="bold" aria-hidden className="auth-lang-option-check" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AuthLanguageSwitcher;
